import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { usersAPI, reviewsAPI } from '../utils/api';
import { ArrowLeft, BadgeCheck, Star, Shield } from 'lucide-react';
import ResponsiveImage from '../components/ResponsiveImage';
import DealerBadge from '../components/DealerBadge';
import TrustedSellerBadge from '../components/TrustedSellerBadge';

const SellerPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [listings, setListings] = useState([]);
  const [reviewInfo, setReviewInfo] = useState({ average_rating: 0, total_reviews: 0, reviews: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [uRes, lRes, rRes] = await Promise.all([
          usersAPI.getPublic(userId),
          usersAPI.getListings(userId),
          reviewsAPI.getSellerReviews(userId).catch(() => ({ data: { average_rating: 0, total_reviews: 0, reviews: [] } }))
        ]);
        if (!active) return;
        setSeller(uRes.data);
        setListings(lRes.data || []);
        setReviewInfo(rRes.data || { average_rating: 0, total_reviews: 0, reviews: [] });
      } catch (_) {
        if (active) navigate('/');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [userId, navigate]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] text-[#6B7280] text-sm">Loading seller profile...</div>;
  }
  if (!seller) return null;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100" data-testid="seller-back-btn">
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-base font-bold text-[#0A0B10]">Seller Profile</h1>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="seller-profile-card">
          <div className="bg-gradient-to-br from-[#1800ad] to-[#ff8500] p-6 flex items-center gap-4">
            {seller.picture ? (
              <img src={seller.picture} alt={seller.name} className="w-20 h-20 rounded-full border-4 border-white/40 object-cover" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center text-white text-3xl font-bold">
                {seller.name?.[0] || '?'}
              </div>
            )}
            <div className="text-white">
              <h2 className="text-xl font-bold flex items-center gap-2" data-testid="seller-name">
                {seller.name}
                {seller.aadhaar_verified && <BadgeCheck size={18} />}
              </h2>
              {reviewInfo.total_reviews > 0 && (
                <div className="flex items-center gap-1 mt-1 text-sm">
                  <Star size={14} className="fill-yellow-300 text-yellow-300" />
                  <span className="font-semibold">{reviewInfo.average_rating}</span>
                  <span className="text-white/80">({reviewInfo.total_reviews} reviews)</span>
                </div>
              )}
            </div>
          </div>
          <div className="p-5 flex flex-wrap gap-2">
            {seller.is_trusted_seller && !seller.is_dealer && (
              <TrustedSellerBadge size="md" testId="seller-trusted-badge" />
            )}
            {seller.is_dealer && (
              <DealerBadge variant="seller" businessName={seller.business_name} testId="seller-dealer-badge" />
            )}
            {seller.aadhaar_verified && (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#f0edff] text-[#1800ad] rounded-full text-xs font-semibold">
                <BadgeCheck size={12} /> Identity Verified
              </span>
            )}
            {seller.phone_verified && (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#fff4ed] text-[#ff8500] rounded-full text-xs font-semibold">
                <Shield size={12} /> Phone Verified
              </span>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 pb-10">
        <h3 className="text-lg font-bold text-[#0A0B10] mb-4">Active Listings ({listings.length})</h3>
        {listings.length === 0 ? (
          <p className="text-sm text-[#6B7280] bg-white rounded-xl p-6 text-center">No active listings</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 items-start">
            {listings.map((listing) => (
              <Link
                key={listing.listing_id}
                to={`/listing/${listing.listing_id}`}
                data-testid={`seller-listing-${listing.listing_id}`}
                className={`brand-gradient-border-card flex flex-col rounded-xl p-2.5 ${listing.is_dealer_listing ? 'dealer-card' : ''}`}
              >
                {listing.images?.[0] ? (
                  <ResponsiveImage
                    path={listing.images[0]}
                    alt={listing.title}
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                    className="w-full h-32 object-cover rounded-lg mb-2"
                  />
                ) : (
                  <div className="w-full h-32 bg-gray-100 rounded-lg mb-2" />
                )}
                <h4 className="text-sm font-bold text-[#0A0B10] line-clamp-2">{listing.title}</h4>
                <p className="text-base font-bold text-[#1800ad] mt-1">{'\u20B9'}{listing.price.toLocaleString()}</p>
                <p className="text-[10px] text-[#9CA3AF]">{listing.location}</p>
                {(listing.is_dealer_listing || listing.is_trusted_seller) && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {listing.is_dealer_listing && <DealerBadge testId={`dealer-badge-${listing.listing_id}`} />}
                    {listing.is_trusted_seller && !listing.is_dealer_listing && (
                      <TrustedSellerBadge size="sm" short testId={`trusted-badge-${listing.listing_id}`} />
                    )}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </section>

      {reviewInfo.reviews?.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 pb-10">
          <h3 className="text-lg font-bold text-[#0A0B10] mb-4">Reviews ({reviewInfo.total_reviews})</h3>
          <div className="space-y-3">
            {reviewInfo.reviews.slice(0, 10).map((r, i) => (
              <div key={i} className="bg-white rounded-xl p-4 border border-gray-100" data-testid={`review-item-${i}`}>
                <div className="flex items-center gap-1 mb-1">
                  {[1,2,3,4,5].map(n => (
                    <Star key={n} size={12} className={n <= r.rating ? 'fill-[#ff8500] text-[#ff8500]' : 'text-gray-300'} />
                  ))}
                </div>
                {r.comment && <p className="text-sm text-[#4B5563]">{r.comment}</p>}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default SellerPage;
